"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import { InstallButton } from "./install-button";
import { installState, isStandalone, watchInstall } from "@/lib/install";
import styles from "./install-nudge.module.css";

/* Asks once, from the bottom, if the app is being used in a browser rather than off a home
   screen.

   The offer already existed in two places and both were easy to never reach: the last panel
   of the welcome, which somebody swipes past in three seconds, and after a first check-in,
   which needs standing inside a business. Somebody who opens the site on their phone and
   uses it for a week was never asked.

   It waits a few seconds rather than appearing on load, because interrupting somebody
   before they have seen the thing is how a prompt gets dismissed on reflex. It is dismissed
   for good once dismissed: a second ask is a nag, and a nag is worse than not being
   installed.

   The card is held back until there is genuinely something to offer. It used to trust that
   InstallButton rendering nothing was enough, which it was not: the card still drew itself
   around the hole, so the whole thing read "Put it on your phone" with a single "Not now"
   underneath and no way to say yes. Asking a question with only one answer is worse than
   not asking. */

const ASKED = "hottip.install.asked";

/* Not on the way in. Six seconds into the welcome, the card rose over the one button on
   the screen, so the first thing a new person was asked was to install something they had
   not seen yet, and the way forward was underneath it. The last welcome panel and the
   sign-up already carry their own offer. This one is for people who are inside. */
const BEFORE_AN_ACCOUNT = ["/welcome", "/signin", "/auth", "/offline"];

export function InstallNudge() {
  const [waited, setWaited] = useState(false);
  const mode = useSyncExternalStore(watchInstall, installState, () => "none" as const);

  useEffect(() => {
    if (isStandalone()) return;

    try {
      if (localStorage.getItem(ASKED)) return;
    } catch {
      // A browser that refuses storage still gets asked. Once per session is not a nag.
    }

    const timer = setTimeout(() => setWaited(true), 6000);
    return () => clearTimeout(timer);
  }, []);

  const path = usePathname() ?? "";
  const onTheWayIn = BEFORE_AN_ACCOUNT.some((p) => path === p || path.startsWith(`${p}/`));

  const [dismissed, setDismissed] = useState(false);
  const show = waited && !dismissed && !onTheWayIn && (mode === "prompt" || mode === "ios");

  function dismiss() {
    setDismissed(true);
    try {
      localStorage.setItem(ASKED, "1");
    } catch {}
  }

  if (!show) return null;

  return (
    <aside className={styles.nudge} aria-label="Put Hot Tip on your phone">
      <div className={styles.copy}>
        <p className={styles.title}>Put it on your phone</p>
        <p className="small soft">
          It opens like an app, works with no signal, and stops you hunting for a tab.
        </p>
      </div>
      <div className={styles.acts}>
        <InstallButton />
        <button type="button" className={styles.no} onClick={dismiss}>Not now</button>
      </div>
    </aside>
  );
}
